import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '../PageHeader'
import { SiteFooter } from '../SiteFooter'
import { BREAKPOINT, ThemeContextValue, uiPrimitives, useThemedStyles } from '../theme'
import {
	AdminCategory,
	AdminLibraryRow,
	AdminPublicScore,
	AdminUserRow,
	Notice,
	adoptScore,
	deleteLibraryItem,
	deleteNotice,
	deleteScore,
	fetchAdminLibrary,
	fetchAdminLibraryItem,
	fetchAdminPublicScores,
	fetchAdminUsers,
	fetchMyScores,
	fetchNotices,
	fetchPlans,
	fetchWatermark,
	formatUsage,
	saveLibraryItem,
	saveNotice,
	saveWatermark,
	setScorePublic,
	setUserPlan,
} from './admin'
import { appConfirm, AppDialogHost } from '../AppDialog'
import { countVoicesInContent } from '../library'
import { musicXmlToNmn } from '../musicxml'

/**
 * 管理后台 —— 会员 / 通知 / 水印 / 教材曲库 / 公开作品。
 *
 * 版面照编曲练习库那一套（`PageHeader` + `useThemedStyles` + `uiPrimitives`），
 * 不另起一套视觉。
 *
 * ⚠️ 「教材曲库」与「公开作品」是**两套数据、两套权限**，别混：
 *   · 教材曲库 = `library_items`（站方内容，无作者），能隐藏/删除/新增；
 *   · 公开作品 = 会员 `scores` 里 `is_public=true` 的（有作者），只能下架/删除，
 *     不能编辑 —— 那是别人的作品。
 * 首页把两者放在同一个区块里（来源切换），但后台必须分开，因为能做的操作不同。
 *
 * 鉴权：路由层已经确认过 isAdmin 才渲染本页（见 TestApp.tsx）。
 * 这里仍然只依赖后端的实际返回 —— 前端那道判断是"要不要显示入口"，
 * 真正的权限在数据库策略与 SECURITY DEFINER 函数里，绕不过去。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	page: {
		padding: '24px 22px',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			padding: '16px 12px',
		},
	},
	tabs: {
		display: 'flex',
		gap: 6,
		marginBottom: 18,
		borderBottom: `1px solid ${t.palette.line}`,
		flexWrap: 'wrap',
	},
	tab: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		padding: '8px 14px',
		border: 0,
		borderBottom: '2px solid transparent',
		marginBottom: -1,
		background: 'none',
		color: t.palette.ink3,
		cursor: 'pointer',
	},
	tabOn: {
		color: t.palette.ink,
		fontWeight: 600,
		borderBottomColor: t.palette.cinnabar,
	},

	// ── 工具条 ──────────────────────────────────────────────
	bar: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		marginBottom: 14,
		flexWrap: 'wrap',
	},
	barTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 600,
		marginRight: 'auto',
	},
	search: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		background: t.palette.paper3,
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		padding: '6px 10px',
		minWidth: 186,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			minWidth: 0,
			flex: '1 1 100%',
		},
	},
	searchInput: {
		border: 0,
		background: 'none',
		font: 'inherit',
		fontFamily: t.fonts.sans,
		fontSize: 13,
		color: t.palette.ink,
		width: '100%',
		outline: 'none',
		'&::placeholder': {
			color: t.palette.ink3,
		},
	},

	// ── 会员表 ──────────────────────────────────────────────
	tableWrap: {
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		overflowX: 'auto',
		background: t.palette.paper2,
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse' as const,
		fontSize: 13,
	},
	th: {
		textAlign: 'left' as const,
		fontFamily: t.fonts.serif,
		fontWeight: 600,
		color: t.palette.ink3,
		padding: '10px 12px',
		borderBottom: `1px solid ${t.palette.line}`,
		whiteSpace: 'nowrap',
	},
	td: {
		padding: '9px 12px',
		borderBottom: `1px solid ${t.palette.line}`,
		color: t.palette.ink,
		verticalAlign: 'middle' as const,
	},
	trLast: {
		'& td': {
			borderBottom: 0,
		},
	},
	mail: {
		fontFamily: t.fonts.mono,
		fontSize: 12.5,
		wordBreak: 'break-all',
	},
	nick: {
		color: t.palette.ink3,
		fontSize: 12,
	},
	planSel: {
		fontFamily: t.fonts.sans,
		fontSize: 12.5,
		padding: '4px 6px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper2,
		color: t.palette.ink,
		cursor: 'pointer',
	},
	pro: {
		color: t.palette.cinnabar,
		fontWeight: 600,
	},
	num: {
		fontVariantNumeric: 'tabular-nums' as const,
		whiteSpace: 'nowrap' as const,
	},
	meta: {
		color: t.palette.ink3,
		fontSize: 11.5,
		whiteSpace: 'nowrap' as const,
	},

	// ── 通知 ────────────────────────────────────────────────
	noticeList: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 10,
		marginBottom: 18,
	},
	noticeItem: {
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		background: t.palette.paper2,
		padding: '13px 15px',
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		flexWrap: 'wrap',
	},
	noticeMain: {
		flex: '1 1 260px',
		minWidth: 0,
	},
	noticeTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 15,
		fontWeight: 600,
		marginBottom: 3,
	},
	noticeBody: {
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink3,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		// 只露两行，完整内容点「编辑」看
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical' as const,
		overflow: 'hidden',
	},
	noticeActs: {
		display: 'flex',
		gap: 6,
		alignItems: 'center',
	},
	draft: {
		fontSize: 11,
		color: t.palette.gold,
		border: `1px solid ${t.palette.gold}`,
		borderRadius: 3,
		padding: '1px 5px',
		whiteSpace: 'nowrap' as const,
	},
	live: {
		fontSize: 11,
		color: t.palette.celadonInk,
		border: `1px solid ${t.palette.celadon}`,
		borderRadius: 3,
		padding: '1px 5px',
		whiteSpace: 'nowrap' as const,
	},
	danger: {
		color: t.palette.cinnabar,
	},

	// ── 表单 ────────────────────────────────────────────────
	form: {
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		background: t.palette.paper2,
		padding: 18,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 14,
	},
	formTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 16,
		fontWeight: 600,
		margin: 0,
	},
	label: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 5,
		fontSize: 12.5,
		color: t.palette.ink2,
	},
	input: {
		fontFamily: t.fonts.sans,
		fontSize: 13,
		padding: '8px 10px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper,
		color: t.palette.ink,
		outline: 'none',
	},
	textarea: {
		fontFamily: t.fonts.sans,
		fontSize: 13,
		lineHeight: 1.6,
		padding: '8px 10px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper,
		color: t.palette.ink,
		outline: 'none',
		resize: 'vertical' as const,
		minHeight: 110,
	},
	row2: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			gridTemplateColumns: '1fr',
		},
	},
	check: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		fontSize: 13,
		color: t.palette.ink2,
		cursor: 'pointer',
	},
	formActs: {
		display: 'flex',
		gap: 8,
		justifyContent: 'flex-end',
		flexWrap: 'wrap',
	},
	hint: {
		fontSize: 12,
		color: t.palette.ink3,
		lineHeight: 1.7,
	},
	err: {
		fontSize: 13,
		color: t.palette.cinnabar,
	},

	empty: {
		padding: '26px 14px',
		textAlign: 'center' as const,
		color: t.palette.ink3,
		fontSize: 13,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.md,
		background: t.palette.paper2,
	},
})

type Tab = 'users' | 'notices' | 'watermark' | 'library' | 'works'
type PlanOpt = {id: string, name: string}

/**
 * 曲目编辑表单的草稿。
 *
 * `id` 有值 = 编辑已有条目（保存时发 id，后端按 id 更新）；
 * 没有 id = 新增（后端生成 slug）。两者走同一个接口，所以表单也只有一套。
 */
type LibDraft = {
	id?: string
	title: string
	composer: string
	category: string
	level: string
	content: string
	is_hidden: boolean
	sort: number
	source: string
	/** 知识点：表单里是**一行文本**（`多声部、转调`），提交时拆成数组。
	 *  用一行输入而不是标签编辑器：这些词是人想的，不是从固定词表挑的 ——
	 *  词表一旦固定，遇到"基调不一致"这种就没处写。 */
	topics: string
	credit: string
}

/**
 * 知识点输入 → 数组。分隔符与后端 `split_topics` 保持一致：
 * 用户贴进来的是 `、`、`，`、`;` 还是 `/` 都能认。
 */
function splitTopics(raw: string): string[] {
	return raw
		.split(/[,，、;；/|]+/)
		.map(s => s.trim())
		.filter(Boolean)
}

const LIB_LEVELS = ['入门', '初级', '中级']

/** 空表单 = 新建一条通知 */
function emptyDraft(): Partial<Notice> {
	return {title: '', body: '', link: '', link_label: '', published: false, sort: 0}
}

export function AdminPage(props: {
	onHome: () => void
	onLogout: () => void
	userName: string
}) {
	const classes = useThemedStyles(stylesFactory)
	const [tab, setTab] = useState<Tab>('users')

	// ---------- 会员 ----------
	const [users, setUsers] = useState<AdminUserRow[] | null>(null)
	const [plans, setPlans] = useState<PlanOpt[]>([])
	const [search, setSearch] = useState('')
	const [userErr, setUserErr] = useState('')
	const [busyUser, setBusyUser] = useState('')

	// ---------- 通知 ----------
	const [notices, setNotices] = useState<Notice[] | null>(null)
	const [draft, setDraft] = useState<Partial<Notice> | null>(null)
	const [noticeErr, setNoticeErr] = useState('')
	const [saving, setSaving] = useState(false)

	// ---------- 水印 ----------
	// watermarkDraft = 输入框里的值（用户正在编辑）；watermarkSaved = 已保存的值（回显）。
	// 两者分开是为了「改了没保存」时能提示，且保存成功后能确认。
	const [watermarkDraft, setWatermarkDraft] = useState<string | null>(null)
	const [watermarkSaved, setWatermarkSaved] = useState<string | null>(null)
	const [watermarkErr, setWatermarkErr] = useState('')
	const [watermarkBusy, setWatermarkBusy] = useState(false)

	// ---------- 教材曲库 ----------
	const [libRows, setLibRows] = useState<AdminLibraryRow[] | null>(null)
	const [libCats, setLibCats] = useState<AdminCategory[]>([])
	const [libSearch, setLibSearch] = useState('')
	const [libCat, setLibCat] = useState('')
	const [libErr, setLibErr] = useState('')
	const [libBusy, setLibBusy] = useState('')
	const [draftLib, setDraftLib] = useState<LibDraft | null>(null)
	const [libSaving, setLibSaving] = useState(false)
	/** 「收录」候选：我自己的云端曲谱 */
	const [myScores, setMyScores] = useState<{id: string, title: string, creator: string}[] | null>(null)
	const uploadRef = useRef<HTMLInputElement | null>(null)

	// ---------- 公开作品 ----------
	const [works, setWorks] = useState<AdminPublicScore[] | null>(null)
	const [workErr, setWorkErr] = useState('')
	const [workBusy, setWorkBusy] = useState('')

	useEffect(() => {
		fetchPlans().then(setPlans)
	}, [])

	async function loadUsers() {
		setUserErr('')
		try {
			setUsers(await fetchAdminUsers())
		} catch (err: any) {
			setUserErr(err?.message ?? '读取会员失败')
			setUsers([])
		}
	}
	async function loadNotices() {
		setNoticeErr('')
		try {
			setNotices(await fetchNotices())
		} catch (err: any) {
			setNoticeErr(err?.message ?? '读取通知失败')
			setNotices([])
		}
	}
	async function loadWatermark() {
		setWatermarkErr('')
		try {
			const text = await fetchWatermark()
			setWatermarkDraft(text)
			setWatermarkSaved(text)
		} catch (err: any) {
			setWatermarkErr(err?.message ?? '读取水印失败')
		}
	}

	// 只在切到那个页签时才拉 —— 后台各页签的数据互不相干，
	// 一进页面就把两边都拉一遍会平白多几次往返。
	useEffect(() => {
		if(tab == 'users') loadUsers()
		else if(tab == 'notices') loadNotices()
		else if(tab == 'watermark') loadWatermark()
		else if(tab == 'library') loadLibrary()
		else if(tab == 'works') loadWorks()
	}, [tab])

	// ---------- 教材曲库：读写 ----------
	/**
	 * 拉列表。
	 *
	 * 接受覆盖值而不是只读 state：分类 chip 的 onClick 里 state 还没更新，
	 * 直接 `setLibCat(id); loadLibrary()` 拿到的是**旧**分类（闭包捕获）。
	 * 用 setTimeout 绕是碰运气 —— React 什么时候 flush 不受这里控制。
	 */
	async function loadLibrary(opts?: {category?: string, q?: string}) {
		const category = opts?.category ?? libCat
		const q = opts?.q ?? libSearch.trim()
		setLibErr('')
		try {
			const data = await fetchAdminLibrary(category, q)
			setLibRows(data.items)
			setLibCats(data.categories)
		} catch (err: any) {
			setLibErr(err?.message ?? '读取曲库失败')
			setLibRows([])
		}
	}

	async function loadWorks() {
		setWorkErr('')
		try {
			setWorks(await fetchAdminPublicScores())
		} catch (err: any) {
			setWorkErr(err?.message ?? '读取公开作品失败')
			setWorks([])
		}
	}

	/** 隐藏/显示：只发这一个字段，后端按"出现在 body 里的字段"逐项更新 */
	async function toggleHidden(row: AdminLibraryRow) {
		setLibBusy(row.id)
		setLibErr('')
		const prev = libRows
		setLibRows(list => (list ?? []).map(r => r.id == row.id ? {...r, is_hidden: !r.is_hidden} : r))
		try {
			await saveLibraryItem({id: row.id, is_hidden: !row.is_hidden})
		} catch (err: any) {
			setLibRows(prev)
			setLibErr(err?.message ?? '操作失败')
		} finally {
			setLibBusy('')
		}
	}

	async function removeLibraryItem(row: AdminLibraryRow) {
		const okDel = await appConfirm({
			title: '删除曲目',
			message: `确定删除「${row.title}」？\n\n这是真删除，行没了，不可恢复。\n只想让首页不显示的话，用「隐藏」——随时可以恢复。`,
			confirmText: '删除',
			cancelText: '取消',
			danger: true,
		})
		if(!okDel) return
		setLibBusy(row.id)
		setLibErr('')
		try {
			await deleteLibraryItem(row.id)
			await loadLibrary()
		} catch (err: any) {
			setLibErr(err?.message ?? '删除失败')
		} finally {
			setLibBusy('')
		}
	}

	/** 编辑：正文单独取（列表接口不带正文，一页 391 首全带会很重） */
	async function editLibraryItem(row: AdminLibraryRow) {
		setLibBusy(row.id)
		setLibErr('')
		try {
			const d = await fetchAdminLibraryItem(row.id)
			setDraftLib({
				id: d.id, title: d.title, composer: d.composer, category: d.category,
				level: d.level, content: d.content, is_hidden: d.is_hidden,
				sort: d.sort, source: d.source,
				// 接口给数组，表单编辑一行文本
				topics: (d.topics ?? []).join('、'),
				credit: d.credit ?? '',
			})
		} catch (err: any) {
			setLibErr(err?.message ?? '读取曲目失败')
		} finally {
			setLibBusy('')
		}
	}

	/** 上传 .musicxml：在浏览器里用现有导入器转成 .spmn，再填进表单让人确认 */
	async function onUploadMusicXml(file: File) {
		setLibErr('')
		try {
			const text = await file.text()
			const r = musicXmlToNmn(text)
			// 导入器只写 `Dt: <work-title>`，而 work-title 常是曲集名。
			// 这里先把文件名的后半段当标题兜底，运营在表单里还能改。
			const dt = /^Dt:\s*(.+)$/m.exec(r.content)?.[1]?.trim() ?? ''
			const ds = /^Ds:\s*(.+)$/m.exec(r.content)?.[1]?.trim() ?? ''
			const fromName = file.name
				.replace(/\.(musicxml|xml)$/i, '')
				.replace(/^(入门|初级|中级|进阶)\s*-\s*[^-]+-\s*/, '')
			setDraftLib({
				title: fromName || dt || '未命名作品',
				composer: ds || '',
				category: libCat || 'misc',
				level: '入门',
				content: r.content,
				is_hidden: false,
				sort: 0,
				source: '上传',
				// 站方上传的东西创建者就是站方；知识点留空，填之前先在表单里补
				topics: '',
				credit: 'manmantan',
			})
			if(r.warnings.length) {
				setLibErr(`已转换，但有 ${r.warnings.length} 条近似告警（不影响保存）：${r.warnings[0]}`)
			}
		} catch (err: any) {
			setLibErr(err?.message ?? 'MusicXML 解析失败')
		}
	}

	async function submitLibraryItem() {
		if(!draftLib) return
		setLibSaving(true)
		setLibErr('')
		try {
			await saveLibraryItem({
				id: draftLib.id,
				title: draftLib.title.trim(),
				composer: draftLib.composer.trim(),
				category: draftLib.category,
				level: draftLib.level,
				content: draftLib.content,
				voices: countVoicesInContent(draftLib.content),
				is_hidden: draftLib.is_hidden,
				sort: draftLib.sort,
				source: draftLib.source,
				topics: splitTopics(draftLib.topics),
				credit: draftLib.credit.trim(),
			})
			setDraftLib(null)
			await loadLibrary()
		} catch (err: any) {
			setLibErr(err?.message ?? '保存失败')
		} finally {
			setLibSaving(false)
		}
	}

	/** 「收录」：把我自己练习库里的一条复制进教材曲库 */
	async function openAdopt() {
		setLibErr('')
		setMyScores(null)
		try {
			setMyScores(await fetchMyScores())
		} catch (err: any) {
			setLibErr(err?.message ?? '读取我的曲谱失败')
			setMyScores([])
		}
	}

	async function doAdopt(scoreId: string, title: string, creator: string) {
		setLibBusy('adopt')
		setLibErr('')
		try {
			await adoptScore({
				score_id: scoreId,
				category: libCat || 'misc',
				level: '入门',
				title,
				composer: creator,
			})
			setMyScores(null)
			await loadLibrary()
		} catch (err: any) {
			setLibErr(err?.message ?? '收录失败')
		} finally {
			setLibBusy('')
		}
	}

	// ---------- 公开作品：下架 / 删除 ----------
	async function togglePublic(row: AdminPublicScore, next: boolean) {
		setWorkBusy(row.id)
		setWorkErr('')
		try {
			await setScorePublic(row.id, next)
			// 下架后这条就不在「公开」列表里了，直接本地移除；恢复则重新拉一遍
			if(next) await loadWorks()
			else setWorks(list => (list ?? []).filter(w => w.id != row.id))
		} catch (err: any) {
			setWorkErr(err?.message ?? '操作失败')
		} finally {
			setWorkBusy('')
		}
	}

	async function removeWork(row: AdminPublicScore) {
		const okDel = await appConfirm({
			title: '删除会员曲谱',
			message: `确定删除「${row.title || '未命名曲谱'}」？\n\n这会连带删掉它的历史版本与分享链接，作者那边也看不到了。\n不可恢复 —— 一般情况请用「下架」。`,
			confirmText: '仍然删除',
			cancelText: '取消',
			danger: true,
		})
		if(!okDel) return
		setWorkBusy(row.id)
		setWorkErr('')
		try {
			await deleteScore(row.id)
			setWorks(list => (list ?? []).filter(w => w.id != row.id))
		} catch (err: any) {
			setWorkErr(err?.message ?? '删除失败')
		} finally {
			setWorkBusy('')
		}
	}

	// ---------- 水印保存 ----------
	async function submitWatermark() {
		if(watermarkDraft == null) return
		setWatermarkBusy(true)
		setWatermarkErr('')
		try {
			await saveWatermark(watermarkDraft)
			setWatermarkSaved(watermarkDraft)
		} catch (err: any) {
			setWatermarkErr(err?.message ?? '保存失败')
		} finally {
			setWatermarkBusy(false)
		}
	}

	async function changePlan(userId: string, planId: string) {
		setBusyUser(userId)
		setUserErr('')
		const prev = users
		// 先本地改掉，失败再回滚 —— 改档位是本后台唯一的高频动作，
		// 等一次往返才看到结果会让人以为没点上而连点
		setUsers(list => (list ?? []).map(u => u.user_id == userId
			? {...u, plan_id: planId, plan_name: plans.find(p => p.id == planId)?.name ?? planId}
			: u))
		try {
			await setUserPlan(userId, planId)
		} catch (err: any) {
			setUsers(prev)
			setUserErr(err?.message ?? '修改档位失败')
		} finally {
			setBusyUser('')
		}
	}

	const filteredUsers = useMemo(() => {
		const q = search.trim().toLowerCase()
		if(!q) return users ?? []
		return (users ?? []).filter(u =>
			(u.email ?? '').toLowerCase().includes(q)
			|| (u.nickname ?? '').toLowerCase().includes(q))
	}, [users, search])

	// ---------- 通知增删改 ----------
	async function submitNotice() {
		if(!draft) return
		setSaving(true)
		setNoticeErr('')
		try {
			// 空串统一转成 null：PostgREST 对 '' 和 null 一样能存，
			// 但后台列表里 "" 会显示成一个空链接按钮
			await saveNotice({
				...draft,
				link: draft.link?.trim() ? draft.link.trim() : null,
				link_label: draft.link_label?.trim() ? draft.link_label.trim() : null,
				title: (draft.title ?? '').trim(),
			})
			setDraft(null)
			await loadNotices()
		} catch (err: any) {
			setNoticeErr(err?.message ?? '保存失败')
		} finally {
			setSaving(false)
		}
	}

	async function removeNotice(id: string, title: string) {
		if(!await appConfirm({title: '删除通知', message: `确定删除通知「${title}」？`, confirmText: '删除', cancelText: '取消', danger: true})) return
		setNoticeErr('')
		try {
			await deleteNotice(id)
			await loadNotices()
		} catch (err: any) {
			setNoticeErr(err?.message ?? '删除失败')
		}
	}

	/** 发布/取消发布、排序 ±1 都走同一条保存路径，不额外开后门 */
	async function patchNotice(n: Notice, patch: Partial<Notice>) {
		setNoticeErr('')
		try {
			await saveNotice({...n, ...patch})
			await loadNotices()
		} catch (err: any) {
			setNoticeErr(err?.message ?? '保存失败')
		}
	}

	return <PageHeader
		doc="管理后台"
		saveState={tab == 'users'
			? (users ? `${users.length} 位会员` : '读取中…')
			: tab == 'notices'
				? (notices ? `${notices.filter(n => n.published).length} 条已发布` : '读取中…')
				: tab == 'library'
					? libErr && !libRows
						? '读取失败'
						// 带筛选时标出「筛选/全库」，否则点了分类后这里显示 96，
						// 会让人以为曲库只剩 96 首。全库数从各分类 count 求和得到
						// （分类 count 是全库口径，不随筛选变）。
						: libRows
							? (libCat || libSearch.trim())
								? `筛选 ${libRows.length} / 全库 ${libCats.reduce((s, c) => s + (c.count || 0), 0)}`
								: `曲库 ${libRows.length} 首`
							: '读取中…'
					: tab == 'works'
						? workErr
							? '读取失败'
							: (works ? `${works.length} 首公开` : '读取中…')
						: (watermarkSaved == null ? '读取中…' : (watermarkSaved ? '水印已启用' : '水印已关闭'))}
		actions={[
			{
				key: 'reload',
				label: '刷新',
				variant: 'outline' as const,
				onClick: () => {
					if(tab == 'users') loadUsers()
					else if(tab == 'notices') loadNotices()
					else if(tab == 'watermark') loadWatermark()
					else if(tab == 'library') loadLibrary()
					else loadWorks()
				},
			},
			...(tab == 'notices' && !draft
				? [{
					key: 'new',
					label: '＋ 新建通知',
					variant: 'primary' as const,
					onClick: () => {
						setDraft(emptyDraft())
						setNoticeErr('')
					},
				}]
				: []),
			...(tab == 'library' && !draftLib
				? [
					{
						key: 'adopt',
						label: '＋ 从练习库收录',
						variant: 'outline' as const,
						onClick: openAdopt,
					},
					{
						key: 'upload',
						label: '＋ 上传 MusicXML',
						variant: 'primary' as const,
						onClick: () => uploadRef.current?.click(),
					},
				]
				: []),
		]}
		onHome={props.onHome}
		trailing={<span style={{fontSize: 12.5}}>{props.userName}</span>}
		onLogout={props.onLogout}>
		<div className={classes.page}>
			<div className={classes.tabs}>
				<button className={classes.tab + (tab == 'users' ? ' ' + classes.tabOn : '')}
					onClick={() => setTab('users')}>会员</button>
				<button className={classes.tab + (tab == 'notices' ? ' ' + classes.tabOn : '')}
					onClick={() => setTab('notices')}>通知</button>
				<button className={classes.tab + (tab == 'library' ? ' ' + classes.tabOn : '')}
					onClick={() => setTab('library')}>教材曲库</button>
				<button className={classes.tab + (tab == 'works' ? ' ' + classes.tabOn : '')}
					onClick={() => setTab('works')}>公开作品</button>
				<button className={classes.tab + (tab == 'watermark' ? ' ' + classes.tabOn : '')}
					onClick={() => setTab('watermark')}>水印</button>
			</div>

			{/* 上传入口藏在这里：PageHeader 的按钮只能触发 click，文件必须由
			    <input type=file> 来选。accept 限一下，免得选到 .mxl（压缩包）后
			    报一个看不懂的解析错误。 */}
			<input
				ref={uploadRef}
				type="file"
				accept=".musicxml,.xml"
				style={{display: 'none'}}
				onChange={e => {
					const f = e.target.files?.[0]
					// 清空 value：同一个文件连选两次也要能触发 onChange
					e.target.value = ''
					if(f) onUploadMusicXml(f)
				}}
			/>

			{tab == 'users' && <>
				<div className={classes.bar}>
					<span className={classes.barTitle}>会员列表</span>
					<label className={classes.search}>
						<input className={classes.searchInput} type="search" value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder="按邮箱 / 昵称搜索" aria-label="按邮箱或昵称搜索" />
					</label>
				</div>
				{userErr && <div className={classes.err} style={{marginBottom: 10}}>{userErr}</div>}
				{users === null ? (
					<div className={classes.empty}>加载中…</div>
				) : filteredUsers.length === 0 ? (
					<div className={classes.empty}>{users.length === 0 ? '还没有会员。' : '没有匹配的会员。'}</div>
				) : (
					<div className={classes.tableWrap}>
						<table className={classes.table}>
							<thead><tr>
								<th className={classes.th}>邮箱</th>
								<th className={classes.th}>昵称</th>
								<th className={classes.th}>档位</th>
								<th className={classes.th}>曲谱</th>
								<th className={classes.th}>本月用量</th>
								<th className={classes.th}>注册时间</th>
							</tr></thead>
							<tbody>
								{filteredUsers.map((u, i) => (
									<tr key={u.user_id} className={i == filteredUsers.length - 1 ? classes.trLast : undefined}>
										<td className={classes.mail}>{u.email ?? '（无邮箱）'}</td>
										<td className={classes.nick}>{u.nickname ?? '—'}</td>
										<td>
											<select className={classes.planSel}
												value={u.plan_id}
												disabled={busyUser === u.user_id}
												aria-label={`${u.email ?? ''} 的档位`}
												onChange={e => changePlan(u.user_id, e.target.value)}>
												{plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
											</select>
										</td>
										<td className={classes.num}>{u.score_count}</td>
										<td className={classes.meta}>{formatUsage(u.month_usage)}</td>
										<td className={classes.meta}>{u.created_at ? u.created_at.slice(0, 10) : '—'}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<p className={classes.hint} style={{marginTop: 12}}>
					改档位立即生效，下一次配额检查（新建曲谱 / AI 识别 / 建分享）就用新限额。
					这里的「曲谱」数是该会员云端曲谱的总数，不受档位限制影响。
				</p>
			</>}

			{tab == 'notices' && <>
				{noticeErr && <div className={classes.err} style={{marginBottom: 10}}>{noticeErr}</div>}
				{notices === null ? (
					<div className={classes.empty}>加载中…</div>
				) : notices.length === 0 && !draft ? (
					<div className={classes.empty}>还没有通知，点右上角「＋ 新建通知」。</div>
				) : (
					<div className={classes.noticeList}>
						{notices.map(n => (
							<div key={n.id} className={classes.noticeItem}>
								<div className={classes.noticeMain}>
									<div className={classes.noticeTitle}>{n.title || '（未命名通知）'}</div>
									{n.body && <div className={classes.noticeBody}>{n.body}</div>}
								</div>
								{n.published
									? <span className={classes.live}>已发布</span>
									: <span className={classes.draft}>草稿</span>}
								<div className={classes.noticeActs}>
									<button className={classes.tb} onClick={() => patchNotice(n, {published: !n.published})}>
										{n.published ? '取消发布' : '发布'}
									</button>
									<button className={classes.tb} onClick={() => {setDraft(n); setNoticeErr('')}}>编辑</button>
									<button className={classes.tb + ' ' + classes.danger}
										onClick={() => removeNotice(n.id, n.title)}>删除</button>
								</div>
							</div>
						))}
					</div>
				)}

				{draft && <div className={classes.form}>
					<h4 className={classes.formTitle}>{draft.id ? '编辑通知' : '新建通知'}</h4>
					<label className={classes.label}>标题（必填）
						<input className={classes.input} value={draft.title ?? ''} required
							placeholder="例如：新增 AI 曲谱识别"
							onChange={e => setDraft({...draft, title: e.target.value})} />
					</label>
					<label className={classes.label}>正文
						<textarea className={classes.textarea} value={draft.body ?? ''}
							placeholder="留空则只显示标题。换行会原样保留。"
							onChange={e => setDraft({...draft, body: e.target.value})} />
					</label>
					<div className={classes.row2}>
						<label className={classes.label}>跳转链接（可留空）
							<input className={classes.input} value={draft.link ?? ''}
								placeholder="https://"
								onChange={e => setDraft({...draft, link: e.target.value})} />
						</label>
						<label className={classes.label}>链接按钮文案（可留空）
							<input className={classes.input} value={draft.link_label ?? ''}
								placeholder="查看详情"
								onChange={e => setDraft({...draft, link_label: e.target.value})} />
						</label>
					</div>
					<div className={classes.row2}>
						<label className={classes.label}>顺序（小的先弹）
							<input className={classes.input} type="number" value={draft.sort ?? 0}
								onChange={e => setDraft({...draft, sort: Number(e.target.value) || 0})} />
						</label>
						<label className={classes.check} style={{alignSelf: 'end', paddingBottom: 8}}>
							<input type="checkbox" checked={!!draft.published}
								onChange={e => setDraft({...draft, published: e.target.checked})} />
							立即发布（不勾则存为草稿，访客看不到）
						</label>
					</div>
					<p className={classes.hint}>
						发布后每台设备都会弹一次；用户看过之后本地记住，不再打扰。
						之后再编辑这条通知，所有人都会重新看到一次。
					</p>
					<div className={classes.formActs}>
						<button className={classes.tb} onClick={() => setDraft(null)}>取消</button>
						<button className={classes.tb + ' ' + classes.tbPrimary}
							disabled={saving || !(draft.title ?? '').trim()}
							onClick={submitNotice}>
							{saving ? '保存中…' : '保存'}
						</button>
					</div>
				</div>}
			</>}

			{/* 教材曲库：站方内容，可隐藏 / 删除 / 新增 / 改标题分类。
			    隐藏只是首页不展示（可恢复），删除是真删行 —— 两个操作分开摆，
			    不给"误点一下就把 391 首之一删掉"的机会。 */}
			{tab == 'library' && <>
				{draftLib ? <>
					<div className={classes.form}>
						<h3 className={classes.formTitle}>{draftLib.id ? `编辑：${draftLib.id}` : '新增曲目'}</h3>
						<div className={classes.row2}>
							<label className={classes.label}>
								曲名
								<input className={classes.input} value={draftLib.title}
									onChange={e => setDraftLib({...draftLib, title: e.target.value})} />
							</label>
							<label className={classes.label}>
								作曲家
								<input className={classes.input} value={draftLib.composer}
									onChange={e => setDraftLib({...draftLib, composer: e.target.value})} />
							</label>
						</div>
						<div className={classes.row2}>
							<label className={classes.label}>
								分类
								<select className={classes.input} value={draftLib.category}
									onChange={e => setDraftLib({...draftLib, category: e.target.value})}>
									{libCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
								</select>
							</label>
							<label className={classes.label}>
								难度
								<select className={classes.input} value={draftLib.level}
									onChange={e => setDraftLib({...draftLib, level: e.target.value})}>
									{LIB_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
								</select>
							</label>
						</div>
						<div className={classes.row2}>
							<label className={classes.label}>
								创建者（卡片上署名）
								<input className={classes.input} value={draftLib.credit}
									placeholder="留空 = 公共领域作品"
									onChange={e => setDraftLib({...draftLib, credit: e.target.value})} />
							</label>
							<label className={classes.label}>
								编谱知识点（顿号或逗号分隔）
								<input className={classes.input} value={draftLib.topics}
									placeholder="多声部、鼓点行、转调、变拍"
									onChange={e => setDraftLib({...draftLib, topics: e.target.value})} />
							</label>
						</div>
						{/* 知识点只有「编谱示例」的卡片会逐条显示；挂在别的分类上
						    不会报错，但也没人看得见 —— 说一句，免得白填。 */}
						{draftLib.category != 'demo' && draftLib.topics.trim() != '' && <p className={classes.hint}>
							知识点只在「编谱示例」分类的卡片上展示，当前分类是
							「{libCats.find(c => c.id == draftLib.category)?.name ?? draftLib.category}」。
						</p>}
						<label className={classes.label}>
							谱面正文（简明记谱，可直接改）
							<textarea className={classes.textarea} style={{minHeight: 220}}
								value={draftLib.content}
								onChange={e => setDraftLib({...draftLib, content: e.target.value})} />
						</label>
						<label className={classes.check}>
							<input type="checkbox" checked={draftLib.is_hidden}
								onChange={e => setDraftLib({...draftLib, is_hidden: e.target.checked})} />
							先隐藏（保存后在首页不可见，可随时放开）
						</label>
						<div className={classes.formActs}>
							<button className={classes.tb} onClick={() => { setDraftLib(null); setLibErr('') }}>
								取消
							</button>
							<button className={classes.tb} disabled={libSaving} onClick={submitLibraryItem}>
								{libSaving ? '保存中…' : '保存'}
							</button>
						</div>
					</div>
					{libErr && <div className={classes.err} style={{marginTop: 10}}>{libErr}</div>}
				</> : <>
					{/* 收录候选：列出自己的云端曲谱，点一首就复制进教材库 */}
					{myScores && <div className={classes.form} style={{marginBottom: 16}}>
						<h3 className={classes.formTitle}>从我的编曲练习库收录</h3>
						<p className={classes.hint}>
							收录会把正文复制一份进教材库（之后两边各自独立：你再改自己的谱不会动到教材）。
							分类沿用当前筛选「{libCats.find(c => c.id == (libCat || 'misc'))?.name ?? '其他钢琴小品'}」，
							难度先记「入门」，可在保存后编辑。
						</p>
						{myScores.length == 0
							? <div className={classes.empty}>你的编曲练习库里还没有曲谱。</div>
							: <div className={classes.noticeList}>
								{myScores.map(s => (
									<div key={s.id} className={classes.noticeItem}>
										<div className={classes.noticeMain}>
											<div className={classes.noticeTitle}>{s.title || '未命名曲谱'}</div>
											{s.creator && <div className={classes.noticeBody}>署名 · {s.creator}</div>}
										</div>
										<button className={classes.tb} disabled={libBusy == 'adopt'}
											onClick={() => doAdopt(s.id, s.title, s.creator)}>
											收入曲库
										</button>
									</div>
								))}
							</div>}
						<div className={classes.formActs}>
							<button className={classes.tb} onClick={() => setMyScores(null)}>关闭</button>
						</div>
					</div>}

					<div className={classes.bar}>
						<span className={classes.barTitle}>曲目列表</span>
						<label className={classes.search}>
							<input className={classes.searchInput} type="search" value={libSearch}
								onChange={e => setLibSearch(e.target.value)}
								onKeyDown={e => { if(e.key == 'Enter') loadLibrary() }}
								placeholder="按曲名 / 作曲家搜索，回车查询"
								aria-label="按曲名或作曲家搜索" />
						</label>
					</div>
					<div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12}}>
						<button className={classes.tb}
							style={libCat == '' ? {borderColor: 'currentColor', fontWeight: 600} : undefined}
							onClick={() => { setLibCat(''); loadLibrary({category: ''}) }}>
							全部分类
						</button>
						{libCats.map(c => <button key={c.id}
							className={classes.tb}
							style={libCat == c.id ? {borderColor: 'currentColor', fontWeight: 600} : undefined}
							onClick={() => { setLibCat(c.id); loadLibrary({category: c.id}) }}>
							{c.name}
							<span style={{opacity: c.count > 0 ? .55 : .3, marginLeft: 4}}>{c.count}</span>
						</button>)}
					</div>
					{/* 同上：读取失败时不再叠一句「没有曲目」，否则会让人以为曲库空了。 */}
					{libErr ? (
						<div className={classes.err}>{libErr}</div>
					) : libRows === null ? (
						<div className={classes.empty}>加载中…</div>
					) : libRows.length === 0 ? (
						<div className={classes.empty}>
							{libCat || libSearch.trim()
								? '这个筛选条件下没有曲目。'
								: '曲库是空的。点右上角「＋ 上传 MusicXML」或「＋ 从练习库收录」添加第一首。'}
						</div>
					) : (
						<div className={classes.tableWrap}>
							<table className={classes.table}>
								<thead><tr>
									<th className={classes.th}>曲名</th>
									<th className={classes.th}>作曲家</th>
									<th className={classes.th}>分类</th>
									<th className={classes.th}>难度</th>
									<th className={classes.th}>声部数</th>
									<th className={classes.th}>状态</th>
									<th className={classes.th}>操作</th>
								</tr></thead>
								<tbody>
									{libRows.map((r, i) => (
										<tr key={r.id} className={i == libRows.length - 1 ? classes.trLast : undefined}>
											<td>
												<div style={{fontFamily: 'inherit'}}>{r.title}</div>
												<div className={classes.meta}>{r.id}</div>
											</td>
											<td className={classes.nick}>{r.composer || '—'}</td>
											<td className={classes.meta}>{r.categoryName}</td>
											<td className={classes.meta}>{r.level}</td>
											<td className={classes.num}>{r.voices}</td>
											<td>{r.is_hidden
												? <span className={classes.draft}>已隐藏</span>
												: <span className={classes.live}>展示中</span>}</td>
											<td>
												<div className={classes.noticeActs}>
													<button className={classes.tb} disabled={libBusy == r.id}
														onClick={() => editLibraryItem(r)}>编辑</button>
													<button className={classes.tb} disabled={libBusy == r.id}
														onClick={() => toggleHidden(r)}>
														{r.is_hidden ? '显示' : '隐藏'}
													</button>
													<button className={classes.tb} disabled={libBusy == r.id}
														style={{color: 'inherit', opacity: .8}}
														onClick={() => removeLibraryItem(r)}>删除</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					{!libErr && <p className={classes.hint} style={{marginTop: 12}}>
						「隐藏」= 首页不展示，随时点「显示」恢复；「删除」是真删行，不可恢复。
						改动立刻生效，首页下次打开就是新状态（前端会缓存一次目录，刷新即可）。
					</p>}
				</>}
			</>}

			{/* 公开作品：会员自己设为公开的曲谱。这里**只能下架/删除**，不能编辑 ——
			    那是别人的作品。默认用「下架」：作者的谱还在他自己的库里，只是不再公开。 */}
			{tab == 'works' && <>
				{/* 有错就只显示错。「还没有人公开」与「读取失败」同时出现会让人以为
				    真的没人公开，而实际是接口挂了 —— 这个 tab 会因此被误判成"没数据"。 */}
				{workErr ? (
					<div className={classes.err}>{workErr}</div>
				) : works === null ? (
					<div className={classes.empty}>加载中…</div>
				) : works.length === 0 ? (
					<div className={classes.empty}>
						还没有会员公开自己的作品。会员在编辑器里保存时把曲谱设为公开，就会出现在这里。
					</div>
				) : (
					<div className={classes.tableWrap}>
						<table className={classes.table}>
							<thead><tr>
								<th className={classes.th}>曲名</th>
								<th className={classes.th}>作者</th>
								<th className={classes.th}>更新时间</th>
								<th className={classes.th}>操作</th>
							</tr></thead>
							<tbody>
								{works.map((w, i) => (
									<tr key={w.id} className={i == works.length - 1 ? classes.trLast : undefined}>
										<td>{w.title || '未命名曲谱'}</td>
										<td className={classes.nick}>{w.nickname || w.creator || '—'}</td>
										<td className={classes.meta}>{w.updated_at ? w.updated_at.slice(0, 10) : '—'}</td>
										<td>
											<div className={classes.noticeActs}>
												<button className={classes.tb} disabled={workBusy == w.id}
													onClick={() => togglePublic(w, false)}>下架</button>
												<button className={classes.tb} disabled={workBusy == w.id}
													style={{color: 'inherit', opacity: .8}}
													onClick={() => removeWork(w)}>删除</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{!workErr && <p className={classes.hint} style={{marginTop: 12}}>
					「下架」把曲谱从首页移走，作者的谱仍在自己的编曲练习库里，他再点「公开」还能重新上架。
					「删除」会连带删掉历史版本与分享链接，作者那边也看不到 —— 只有确实要清掉的数据才用。
				</p>}
			</>}

			{/* 水印：一张斜向平铺的水印铺在曲谱预览与各导出出口上。
			    这里只改**文字**，形式（斜向平铺 / 深浅主题 / 密度）由组件固定。 */}
			{tab == 'watermark' && <>
				{watermarkErr && <div className={classes.err} style={{marginBottom: 10}}>{watermarkErr}</div>}
				{watermarkDraft == null ? (
					<div className={classes.empty}>加载中…</div>
				) : (
					<div className={classes.form}>
						<h4 className={classes.formTitle}>曲谱水印文案</h4>
						<label className={classes.label}>水印文字
							<input className={classes.input} value={watermarkDraft}
								placeholder="例如：漫漫弹 https://manmantan.xyz"
								onChange={e => setWatermarkDraft(e.target.value)} />
						</label>
						<p className={classes.hint}>
							铺在编辑页曲谱预览、分享页，以及导出的 PDF 上。
							形式（斜向平铺、随深浅主题变色、密度）固定不变，这里只改文字。<br />
							留空即整块关闭水印。修改后新打开的谱子立即生效（含分享页）。
						</p>
						<div className={classes.formActs}>
							<button className={classes.tb}
								disabled={watermarkBusy || watermarkDraft === watermarkSaved}
								onClick={() => {setWatermarkDraft(watermarkSaved ?? ''); setWatermarkErr('')}}>还原</button>
							<button className={classes.tb + ' ' + classes.tbPrimary}
								disabled={watermarkBusy || watermarkDraft === watermarkSaved}
								onClick={submitWatermark}>
								{watermarkBusy ? '保存中…' : '保存'}
							</button>
						</div>
					</div>
				)}
			</>}


			<SiteFooter />
			{/* 站内确认对话框宿主（删除通知等）；与编辑器页同一套宣纸/暗墨弹窗 */}
			<AppDialogHost />
		</div>
	</PageHeader>
}